import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertActionLink,
  Breadcrumb,
  BreadcrumbItem,
  Bullseye,
  Content,
  EmptyState,
  EmptyStateBody,
  Icon,
  Label,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { LockIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { useApiFetch } from '../../../api';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { CertificatePlatform, ClientSummary } from '../../../types/models';
import type { ApiResponseCertificatePlatformList } from '../../../types/api/ApiResponseCertificatePlatform';
import type { ApiResponseClientList } from '../../../types/api/ApiResponseClient';
import CertificatesOverview from './CertificatesOverview';
import CertificatePlatformTabContent from './CertificatePlatformTabContent';

function Certificates() {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const user = useUserContext();
  const { isImpersonating } = useImpersonation();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();

  const canManageClientAssignment = user?.is_ancalagon_staff === true && !isImpersonating;

  const [platforms, setPlatforms] = useState<CertificatePlatform[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Seed visited platforms from the initial URL so a deep link such as
  // `?tab=4&status=expired` renders the matching tab body on first paint,
  // not after a follow-up effect. New tabs the user clicks are added via
  // the onSelect handler below.
  const [visitedPlatformIds, setVisitedPlatformIds] = useState<Set<number>>(() => {
    const tab = searchParams.get('tab');
    if (tab === null) {
      return new Set();
    }
    const id = Number(tab);
    return Number.isInteger(id) ? new Set([id]) : new Set();
  });
  const [clients, setClients] = useState<ClientSummary[] | null>(null);
  // Tracks the in-flight state of the single `/clients` request.
  // `clients === null && !isClientsLoading` means the fetch resolved with an error
  // (see §2.5 in the prompt — child needs this to render "Failed to load clients"
  // versus "Loading clients…" in the Assign-to submenu).
  const [isClientsLoading, setIsClientsLoading] = useState<boolean>(false);

  const tabParam = searchParams.get('tab');
  const tabIdCandidate = useMemo(() => {
    if (tabParam === null) {
      return null;
    }
    const id = Number(tabParam);
    return Number.isInteger(id) ? id : null;
  }, [tabParam]);

  // Derive activeTabKey from the URL. Once platforms are loaded, an unknown
  // id falls back to 'overview' so the tab strip never highlights a
  // non-existent tab. While platforms are still loading we trust the URL —
  // the spinner is showing anyway and the highlight will resolve on next
  // render.
  const activeTabKey: string | number = useMemo(() => {
    if (tabIdCandidate === null) {
      return 'overview';
    }
    if (platforms !== null && !platforms.some((platform) => platform.id === tabIdCandidate)) {
      return 'overview';
    }
    return tabIdCandidate;
  }, [tabIdCandidate, platforms]);

  const fetchPlatforms = useCallback(async () => {
    setPlatforms(null);
    setError(null);
    try {
      const response = await apiFetch('/datacenters/certificate-platforms');
      let body: ApiResponseCertificatePlatformList | null;
      try {
        body = (await response.json()) as ApiResponseCertificatePlatformList;
      } catch {
        body = null;
      }
      if (!response.ok || !body || body.status !== 'success' || !body.data) {
        const message = body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
        setError(message);
        return;
      }
      const list = body.data.certificate_platforms ?? [];
      const sorted = [
        ...list.filter((platform) => platform.clientID === null),
        ...list.filter((platform) => platform.clientID !== null),
      ];
      setPlatforms(sorted);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected network error occurred.');
    }
  }, [apiFetch]);

  const fetchClients = useCallback(async () => {
    setIsClientsLoading(true);
    try {
      const response = await apiFetch('/clients');
      let body: ApiResponseClientList | null;
      try {
        body = (await response.json()) as ApiResponseClientList;
      } catch {
        body = null;
      }
      if (!response.ok || !body || body.status !== 'success' || !body.data) {
        const message = body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
        console.error('Failed to load clients:', message);
        addNotification({
          title: 'Could not load client list',
          srTitle: 'Client list error',
          description: `${message} — assigning certificates to clients will be unavailable until the page is reloaded.`,
          variant: 'danger',
        });
        setDrawerOpen(true);
        return;
      }
      setClients(body.data.clients ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected network error occurred.';
      console.error('Failed to load clients:', message);
      addNotification({
        title: 'Could not load client list',
        srTitle: 'Client list error',
        description: `${message} — assigning certificates to clients will be unavailable until the page is reloaded.`,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsClientsLoading(false);
    }
  }, [apiFetch, addNotification, setDrawerOpen]);

  useEffect(() => {
    void fetchPlatforms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the client list exactly once, only when the real user is staff and
  // not impersonating. Customers (or staff while impersonating) never see
  // GET /clients in their network tab — the backend would 403 anyway, but
  // gating here avoids polluting logs and prevents the column/actions from
  // having any partial data to render.
  // No refresh affordance is intentional (§2.4): if staff add a new client
  // and want it in the Assign-to submenu, they reload the page.
  useEffect(() => {
    if (!canManageClientAssignment) {
      return;
    }
    void fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageClientAssignment]);

  // Mark a URL-supplied platform id as visited whenever it resolves to a real
  // platform. Covers both deep-load (?tab=N already in the URL on mount) and
  // client-side navigation from the dashboard's bucket links, which change the
  // URL after mount and so are not caught by the useState initializer.
  useEffect(() => {
    if (tabIdCandidate === null || platforms === null) {
      return;
    }
    if (!platforms.some((platform) => platform.id === tabIdCandidate)) {
      return;
    }
    setVisitedPlatformIds((prev) => (prev.has(tabIdCandidate) ? prev : new Set(prev).add(tabIdCandidate)));
  }, [tabIdCandidate, platforms]);

  const handleTabSelect = useCallback(
    (_event: React.MouseEvent<HTMLElement, MouseEvent>, eventKey: string | number) => {
      if (typeof eventKey === 'number') {
        setVisitedPlatformIds((prev) => (prev.has(eventKey) ? prev : new Set(prev).add(eventKey)));
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('tab', String(eventKey));
            return next;
          },
          { replace: true },
        );
      } else {
        // Selecting Overview clears both `tab` and `status` so the dashboard
        // is fully reset (mirrors the "back to home" mental model).
        setSearchParams({}, { replace: true });
      }
    },
    [setSearchParams],
  );

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const header = (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem isActive>Certificates</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <LockIcon />
          </Icon>
          Certificates
        </Content>
        <Content component="p">Manage your TLS/SSL certificates</Content>
      </PageSection>
    </>
  );

  if (platforms === null && error === null) {
    return (
      <>
        {header}
        <PageSection hasBodyWrapper={false}>
          <Bullseye>
            <Spinner size="xl" aria-label="Loading certificate platforms" />
          </Bullseye>
        </PageSection>
      </>
    );
  }

  if (error !== null) {
    return (
      <>
        {header}
        <PageSection hasBodyWrapper={false}>
          <Alert
            variant="danger"
            isInline
            title="Failed to load certificate platforms"
            actionLinks={<AlertActionLink onClick={() => void fetchPlatforms()}>Retry</AlertActionLink>}
          >
            {error}
          </Alert>
        </PageSection>
      </>
    );
  }

  if (platforms !== null && platforms.length === 0) {
    return (
      <>
        {header}
        <PageSection hasBodyWrapper={false}>
          <EmptyState titleText="No certificate platforms available" icon={LockIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>Contact Ancalagon to enable certificate management for your organization.</EmptyStateBody>
          </EmptyState>
        </PageSection>
      </>
    );
  }

  const platformList = platforms ?? [];

  return (
    <>
      {header}
      <PageSection hasBodyWrapper={false}>
        <Tabs activeKey={activeTabKey} onSelect={handleTabSelect} isBox aria-label="Certificate platforms">
          {[
            <Tab key="overview" eventKey="overview" title={<TabTitleText>Overview</TabTitleText>}>
              <CertificatesOverview />
            </Tab>,
            ...platformList.map((platform) => (
              <Tab
                key={platform.id}
                eventKey={platform.id}
                title={
                  <>
                    <TabTitleText>{platform.name}</TabTitleText>
                    {platform.clientID === null && (
                      <Label color="blue" isCompact style={{ marginLeft: '8px' }}>
                        Shared
                      </Label>
                    )}
                  </>
                }
              >
                {visitedPlatformIds.has(platform.id) ? (
                  <CertificatePlatformTabContent
                    platform={platform}
                    clients={clients}
                    isClientsLoading={isClientsLoading}
                    canManageClientAssignment={canManageClientAssignment}
                  />
                ) : null}
              </Tab>
            )),
          ]}
        </Tabs>
      </PageSection>
    </>
  );
}

export { Certificates };
